import React, { memo } from "react";
import { 
    IconButton, List, ListItemIcon, ListItemButton, Divider, 
    ListItem, ListItemText, useTheme, Box, alpha, Typography
} from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DrawerHeader, Drawer, LOCATION_ROOT, LOCATION_ADMIN, LOCATION_TUTOR, BootstrapTooltip } from "../Helper/SidebarHelper";
import { useDispatch, useSelector } from "react-redux";
import { setLocation } from "../Reducers/navigation";

const SideBar = ({ navState, openNav }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const permissions = useSelector((state) => state.role);
    const location = useSelector((state) => state.navigation.location);

    const changeLocation = (key) => {
        dispatch(setLocation(key));
        // On mobile/small screens, you might want to auto-close
        // openNav(false); 
    };

    // Helper to render navigation items to avoid repeating code
    const renderNavItems = (items) => (
        Object.entries(items).map(([key, value]) => {
            const isSelected = key === location;
            return (
                <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }} key={key}>
                    <BootstrapTooltip title={!navState ? key : ""} placement="right">
                        <ListItemButton
                            selected={isSelected}
                            onClick={() => changeLocation(key)}
                            sx={{
                                minHeight: 48,
                                justifyContent: navState ? 'initial' : 'center',
                                px: 2.5,
                                mx: navState ? 1 : 0, // Margin when open for "pill" effect
                                borderRadius: navState ? '12px' : '0px',
                                transition: 'all 0.2s ease-in-out',
                                // Active state styles
                                '&.Mui-selected': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                    color: theme.palette.primary.main,
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.25),
                                    },
                                    '& .MuiListItemIcon-root': {
                                        color: theme.palette.secondary.main,
                                    }
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: navState ? 2 : 'auto',
                                    justifyContent: 'center',
                                    color: isSelected ? 'inherit' : 'text.secondary',
                                }}
                            >
                                {value.icon}
                            </ListItemIcon>
                            <ListItemText 
                                primary={key} 
                                sx={{ 
                                    opacity: navState ? 1 : 0,
                                    '& .MuiTypography-root': {
                                        fontWeight: isSelected ? 700 : 500,
                                        fontSize: '0.875rem'
                                    }
                                }} 
                            />
                        </ListItemButton>
                    </BootstrapTooltip>
                </ListItem>
            );
        })
    );

    return (
        <Drawer 
            variant="permanent" 
            open={navState}
            sx={{
                '& .MuiDrawer-paper': {
                    // Glassmorphism logic to match Navbar
                    backgroundColor: alpha(theme.palette.background.default, 0.8),
                    backdropFilter: 'blur(10px)',
                    borderRight: `1px solid ${theme.palette.divider}`,
                    boxShadow: navState ? '4px 0px 10px rgba(0,0,0,0.05)' : 'none',
                    transition: theme.transitions.create(['width', 'background-color'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }
            }}
        >
            <DrawerHeader>
                <IconButton onClick={() => openNav(false)}>
                    {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
            </DrawerHeader>
            
            <Divider sx={{ opacity: 0.5 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', pt: 1 }}>
                <List sx={{ flexGrow: 1, px: navState ? 1 : 0 }}>
                    {permissions.role === "ROOT" && renderNavItems(LOCATION_ROOT)}
                    {permissions.role === "ADMIN" && renderNavItems(LOCATION_ADMIN)}
                    {permissions.role === "TUTOR" && renderNavItems(LOCATION_TUTOR)}
                </List>
                
                {/* Optional: Footer section for Sidebar (e.g., Settings or Help) */}
                <Box sx={{ pb: 2, display: 'flex', justifyContent: 'center' }}>
                    <Typography variant="caption" sx={{ opacity: navState ? 0.5 : 0, fontSize: '10px' }}>
                        v2.0.4-beta
                    </Typography>
                </Box>
            </Box>
        </Drawer>
    );
};

export default memo(SideBar);